import type { MapMarker } from '@gpx-spa/geo-domain';
import type { ShipmentExceptionRow } from '@gpx-spa/queries/src/shipment-exception-types';
import {
  namedTrackersFromIds,
  type NamedTracker,
} from '@/utils/named-tracker-set';
import { exceptionMapPopupHtml } from './popup-html';
import { exceptionMapMarkerStyle } from './style';

export type ShipmentExceptionsToMapMarkersOptions = {
  /** When true, closed rows with a valid location are included. Default false. */
  includeClosed?: boolean;
  /** Shipment devices used to label `device_ids` in the popup roster. */
  devices?: readonly NamedTracker[];
};

function isFiniteLocation(
  location: ShipmentExceptionRow['location'],
): location is { lat: number; lng: number } {
  return (
    location != null &&
    Number.isFinite(location.lat) &&
    Number.isFinite(location.lng)
  );
}

/**
 * Exception rows → map markers (one per row with a finite location).
 * Vue should call this facade only — no catalog/style in the map component.
 */
export function shipmentExceptionsToMapMarkers(
  rows: readonly ShipmentExceptionRow[],
  options: ShipmentExceptionsToMapMarkersOptions = {},
): MapMarker[] {
  const includeClosed = options.includeClosed === true;
  const devices = options.devices ?? [];
  const out: MapMarker[] = [];
  for (const row of rows) {
    if (!includeClosed && row.status !== 'open') continue;
    if (!isFiniteLocation(row.location)) continue;
    const trackers = namedTrackersFromIds(row.device_ids ?? [], devices);
    out.push({
      id: `exception-${row.id}`,
      position: { lat: row.location.lat, lng: row.location.lng },
      style: exceptionMapMarkerStyle(row.severity),
      popup: exceptionMapPopupHtml(row, trackers),
      popupHtml: true,
    });
  }
  return out;
}
